import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import ReverseImageSearchButton from "../components/ReverseImageSearchButton";
import MediaPlayerModal from "../components/MediaPlayerModal";
import { Play, Lock, Search, X, ImagePlus, FolderPlus, MessageCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Trophy from "../components/Trophy";

const MEDIA_TYPES = [
  { k: "all",   tk: "gallery.filter_all" },
  { k: "image", tk: "gallery.filter_images" },
  { k: "video", tk: "gallery.filter_video" },
];
const SORTS = [
  { k: "recent",         tk: "gallery.sort_latest" },
  { k: "popular",        tk: "gallery.sort_popular" },
  { k: "trending",       tk: "gallery.sort_trending" },
  { k: "most_commented", tk: "gallery.sort_commented" },
];
const GENDERS = [
  { k: "",       tk: "gallery.filter_all" },
  { k: "kvinne", tk: "gallery.gender_women" },
  { k: "mann",   tk: "gallery.gender_men" },
  { k: "par",    tk: "gallery.gender_couples" },
  { k: "trans",  tk: "gallery.gender_trans" },
];

const Pill = ({ active, onClick, children, testId, small = true }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={active ? undefined : undefined}
    className={`${small ? "px-2.5 py-1 text-[9px]" : "px-3 py-1.5 text-[10px]"} uppercase tracking-[0.2em] font-manrope transition-colors whitespace-nowrap ${
      active
        ? "bg-[var(--cream)] text-black"
        : "bg-transparent text-[var(--cream-soft)] border border-white/10 hover:border-[var(--sage)] hover:text-[var(--sage)]"
    }`}
  >
    {children}
  </button>
);

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const tierRank = { free: 0, vip: 1, creator: 2 };

export default function Bilder() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const BILDER_SS_KEY = "bilder_filter_v1";
  const bildInit = (() => {
    try { return JSON.parse(sessionStorage.getItem(BILDER_SS_KEY) || "{}") || {}; }
    catch { return {}; }
  })();
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [mediaType, setMediaType] = useState(bildInit.mediaType || "all");
  const [sort, setSort] = useState(bildInit.sort || "recent");
  const [gender, setGender] = useState(bildInit.gender || "");
  const [tag, setTag] = useState(bildInit.tag || "");
  const [tagInput, setTagInput] = useState(bildInit.tag || "");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeMedia, setActiveMedia] = useState(null);
  const [trophyMap, setTrophyMap] = useState({}); // id -> {place, contest_id, contest_title, ...}

  const PAGE_SIZE = 60;

  useEffect(() => {
    try { sessionStorage.setItem(BILDER_SS_KEY, JSON.stringify({ mediaType, sort, gender, tag })); }
    catch { /* ignore */ }
  }, [mediaType, sort, gender, tag]);

  // Hvis URL har ?image=<id>, hent bildet og åpne lightboxen automatisk.
  // Brukes av varsler (kommentar på bilde, like osv.).
  useEffect(() => {
    const wantId = searchParams.get("image");
    if (!wantId) return;
    api.get(`/images/${wantId}/single`)
      .then((r) => {
        setActiveMedia(r.data);
        setItems((curr) => (curr.some((i) => i.id === r.data.id) ? curr : [r.data, ...curr]));
      })
      .catch(() => { /* fortsetter uten å åpne hvis bildet er borte/skjult */ });
  }, [searchParams]);

  useEffect(() => {
    api.get("/tags/popular").then((r) => setTags(r.data)).catch(() => {});
  }, []);

  // Live tag-søk: oppdater tag-state ved hver tastetrykk (debounced via React-render)
  useEffect(() => {
    const cleaned = tagInput.replace(/^#+/, "").trim().toLowerCase();
    const t = setTimeout(() => setTag(cleaned), 200);
    return () => clearTimeout(t);
  }, [tagInput]);

  useEffect(() => {
    setLoading(true);
    setHasMore(true);
    const params = new URLSearchParams({
      media_type: mediaType,
      sort,
      limit: String(PAGE_SIZE),
      skip: "0",
    });
    if (gender) params.set("gender", gender);
    if (tag) params.set("tag", tag);
    api
      .get(`/feed/media?${params}`)
      .then((r) => {
        const arr = r.data || [];
        setItems(arr);
        setHasMore(arr.length >= PAGE_SIZE);
      })
      .finally(() => setLoading(false));
  }, [mediaType, sort, gender, tag]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        media_type: mediaType,
        sort,
        limit: String(PAGE_SIZE),
        skip: String(items.length),
      });
      if (gender) params.set("gender", gender);
      if (tag) params.set("tag", tag);
      const r = await api.get(`/feed/media?${params}`);
      const arr = r.data || [];
      setItems((prev) => [...prev, ...arr]);
      setHasMore(arr.length >= PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  // Hent troféer for synlig innhold (batch lookup)
  useEffect(() => {
    if (!items || items.length === 0) { setTrophyMap({}); return; }
    // Splitt på media_type – kontests har forskjellig type for image og video
    const imageIds = items.filter((i) => i.media_type !== "video").map((i) => i.id);
    const videoIds = items.filter((i) => i.media_type === "video").map((i) => i.id);
    const calls = [];
    if (imageIds.length) calls.push(api.post("/contests/content-trophies", { type: "image", ids: imageIds }));
    if (videoIds.length) calls.push(api.post("/contests/content-trophies", { type: "video", ids: videoIds }));
    Promise.all(calls).then((rs) => {
      const merged = {};
      rs.forEach((r) => Object.assign(merged, r.data?.results || {}));
      setTrophyMap(merged);
    }).catch(() => setTrophyMap({}));
  }, [items]);

  const userTier = user && user !== false ? (user.is_creator ? "creator" : user.tier || "free") : "free";

  const isLoggedOut = !user || user === false;
  const isOwner = user?.role === "owner";
  const [sourceCheckMode, setSourceCheckMode] = useState(false);
  const isLocked = (item) => {
    // Utloggede brukere ser alt blurret (18+-vegg)
    if (isLoggedOut) return true;
    if (item.locked) return true;
    const required = item.requires_tier || "free";
    return tierRank[required] > tierRank[userTier];
  };

  const setTagAndInput = (t) => {
    setTagInput(t);
  };

  return (
    <Layout withSidebar={false}>
      {/* Kompakt header: tittel venstre, kontroller høyre */}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-x-12 gap-y-6 mb-8 items-start">
        {/* Venstre: tittel */}
        <div>
          <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] leading-tight">
            {t("gallery.page_title")}.
          </h1>
        </div>

        {/* Høyre: alle filtre + søk i kompakt blokk – maks 2 linjer */}
        <div className="lg:pt-3 space-y-3 panel-galleri" style={{ padding: "14px 18px" }} data-testid="filter-bar">
          {/* Linje 1: Type + Sortering + søk */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex items-center gap-1.5">
              <span className="font-meta mr-1">{t("gallery.filter_type")}</span>
              {MEDIA_TYPES.map((m) => (
                <Pill key={m.k} active={mediaType === m.k} onClick={() => setMediaType(m.k)} testId={`mediatype-${m.k}`}>
                  {t(m.tk)}
                </Pill>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-meta mr-1">{t("gallery.filter_sort")}</span>
              {SORTS.map((s) => (
                <Pill key={s.k} active={sort === s.k} onClick={() => setSort(s.k)} testId={`sort-${s.k}`}>
                  {t(s.tk)}
                </Pill>
              ))}
            </div>

            {/* Tag-søk – flex-grow så det legger seg til høyre */}
            <div className="relative flex-1 min-w-[180px] sm:max-w-xs ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--cream-faint)] pointer-events-none" />
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder={t("gallery.search_tag")}
                className="w-full bg-transparent border border-white/10 hover:border-white/20 focus:border-[var(--sage)] focus:outline-none px-7 py-1.5 text-[11px] uppercase tracking-[0.15em] font-manrope text-[var(--cream)] placeholder:text-[var(--cream-faint)]/60 transition-colors"
                data-testid="tag-search"
              />
              {tagInput && (
                <button
                  type="button"
                  onClick={() => setTagInput("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
                  data-testid="tag-search-clear"
                  aria-label="Tøm søk"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Linje 2: Kjønn + populære tags – holdes på én linje, scroll hvis nødvendig */}
          <div className="flex items-center gap-x-5 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="font-meta mr-1">{t("gallery.filter_gender")}</span>
              {GENDERS.map((g) => (
                <Pill key={g.k || "all"} active={gender === g.k} onClick={() => setGender(g.k)}>
                  {t(g.tk)}
                </Pill>
              ))}
            </div>
            {tags.length > 0 && (
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="font-meta mr-1">Tags</span>
                <Pill active={tag === ""} onClick={() => setTagAndInput("")}>{t("gallery.filter_all")}</Pill>
                {tags.slice(0, 5).map((t) => (
                  <Pill key={t.tag} active={tag === t.tag} onClick={() => setTagAndInput(t.tag)}>
                    #{t.tag}
                  </Pill>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <div className="font-fraunces italic text-2xl text-[var(--cream-soft)] mb-2">
            {tag ? `Ingen treff på #${tag}.` : "Tomt galleri."}
          </div>
          <p className="font-manrope text-sm text-[var(--cream-faint)] max-w-md mx-auto">
            {tag ? (
              <>
                Prøv en annen tag, eller{" "}
                <button onClick={() => setTagInput("")} className="text-[var(--magenta)] hover:underline">
                  vis alle
                </button>.
              </>
            ) : (
              <>
                Bli den første til å dele offentlig innhold – gå til{" "}
                <Link to="/albums" className="text-[var(--magenta)]">album</Link> og marker det som «Offentlig» ved opplasting.
              </>
            )}
          </p>
        </div>
      ) : (
        <>
        {/* Owner-only: kildesjekk-modus (reverse image search) */}
        {isOwner && (
          <div className="mb-3 flex items-center justify-end gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sourceCheckMode}
                onChange={(e) => setSourceCheckMode(e.target.checked)}
                className="h-4 w-4"
                data-testid="source-check-toggle"
              />
              <span className={`font-meta text-[11px] uppercase tracking-widest ${sourceCheckMode ? "text-[var(--magenta)]" : "text-[var(--cream-soft)]"}`}>
                🔍 Kildesjekk-modus {sourceCheckMode && "· PÅ"}
              </span>
            </label>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5" data-testid="media-feed">
          {items.map((item) => {
            const locked = isLocked(item);
            const isVideo = item.media_type === "video";
            const vipLocked = isVideo && item.can_view === false;
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (vipLocked) { window.location.href = "/vip"; return; }
                  if (!locked) {
                    setActiveMedia(item);
                    const next = new URLSearchParams(searchParams);
                    next.set("image", item.id);
                    setSearchParams(next, { replace: false });
                  }
                }}
                className="group relative aspect-[3/4] overflow-hidden bg-black ring-1 ring-white/5 hover:ring-[var(--sage)]/60 transition-all text-left"
                data-testid={`feed-${isVideo ? "video" : "image"}-${item.id}`}
              >
                {item.locked ? <div className="w-full h-full bg-zinc-900" /> : <AuthImage
                  imageId={item.id}
                  alt={item.caption || ""}
                  className={`w-full h-full ${(locked || vipLocked) ? "object-cover" : "object-contain"} ${vipLocked ? "blur-md scale-110" : ""} ${locked ? "guest-blur-target" : ""} ${item.nsfw_category === "erotisk" ? "erotic-blur-target" : ""}`}
                />}
                {!locked && !vipLocked && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent pointer-events-none" />
                )}
                {locked && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                )}
                {vipLocked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm gap-2" data-testid="vip-video-gate">
                    <span className="px-2 py-0.5 bg-[var(--gold)] text-[var(--noir)] text-[10px] font-bold uppercase tracking-wider">VIP</span>
                    <span className="font-fraunces italic text-sm text-[var(--cream)]">Krever VIP eller venneskap</span>
                  </div>
                )}

                {isVideo && !locked && !vipLocked && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="h-10 w-10 rounded-full bg-black/60 ring-1 ring-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-[var(--magenta)] transition-colors">
                      <Play className="h-4 w-4 text-white ml-0.5" />
                    </div>
                  </div>
                )}

                {isVideo && (
                  <div className="absolute top-1.5 right-1.5">
                    <span className="font-meta text-[9px] bg-black/70 px-1.5 py-0.5 text-[var(--cream)]">
                      {item.duration_seconds ? formatDuration(item.duration_seconds) : "VIDEO"}
                    </span>
                  </div>
                )}

                {/* Trofé-medalje hvis innholdet har vunnet i en konkurranse */}
                {trophyMap[item.id] && (
                  <span
                    className={`absolute z-10 ${isVideo ? "top-7" : "top-1.5"} right-1.5 pointer-events-auto`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trophy
                      place={trophyMap[item.id].place}
                      contestTitle={trophyMap[item.id].contest_title}
                      contestId={trophyMap[item.id].contest_id}
                      variant="corner"
                      size={16}
                    />
                  </span>
                )}

                {locked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="h-9 w-9 rounded-full bg-[var(--magenta)]/90 flex items-center justify-center mb-1.5">
                      <Lock className="h-4 w-4 text-black" />
                    </div>
                    <div className="font-meta text-[9px] text-[var(--cream)] uppercase tracking-[0.2em]">
                      {isLoggedOut
                        ? "Bli medlem"
                        : item.locked === "paid" ? `Betalt · ${item.price_tokens}`
                        : item.requires_tier === "creator" ? "Creator" : "VIP"}
                    </div>
                  </div>
                )}

                <div className="absolute left-0 right-0 bottom-0 p-2">
                  <Link
                    to={`/profile/${item.owner.username}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-fraunces italic text-base text-[var(--cream)] hover:text-[var(--magenta)] block leading-tight truncate"
                  >
                    {item.owner.username}
                  </Link>
                  {(item.owner.city || item.owner.age) && (
                    <div className="font-meta text-[9px] truncate">
                      {item.owner.city}{item.owner.city && item.owner.age ? " · " : ""}{item.owner.age}
                    </div>
                  )}
                </div>

                {/* Kommentar-teller (nede til høyre) – vises bare når det finnes minst én kommentar */}
                {!vipLocked && item.comment_count > 0 && (
                  <div
                    className="absolute bottom-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/70 backdrop-blur-sm ring-1 ring-white/10 font-meta text-[10px] text-[var(--cream)]"
                    data-testid={`comment-count-${item.id}`}
                  >
                    <MessageCircle className="h-2.5 w-2.5 text-[var(--magenta)]" />
                    {item.comment_count}
                  </div>
                )}

                {/* Owner-only kildesjekk-knapp (aktiv i sourceCheckMode) */}
                {isOwner && sourceCheckMode && !isVideo && (
                  <ReverseImageSearchButton imageId={item.id} />
                )}
              </button>
            );
          })}
        </div>
        {/* Vis flere / slutt-melding */}
        <div className="mt-8 flex flex-col items-center gap-3">
          {hasMore ? (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="btn-magenta text-sm px-6 py-3 disabled:opacity-60"
              data-testid="bilder-load-more"
            >
              {loadingMore ? "Laster…" : "Vis flere"}
            </button>
          ) : (
            <p className="font-fraunces italic text-sm text-[var(--cream-soft)]">
              Du har sett alt.
            </p>
          )}
        </div>
        </>
      )}

      <MediaPlayerModal media={activeMedia} items={items} onClose={() => {
        setActiveMedia(null);
        if (searchParams.get("image")) {
          const next = new URLSearchParams(searchParams);
          next.delete("image");
          setSearchParams(next, { replace: true });
        }
      }} />
    </Layout>
  );
}
